import { CityT } from '@/core/types/cities.types'
import { TemplatePayloadT } from '@/core/types/templates.types'

import { useMaska } from '@/common/composable/useMaska'
import { CompletedCargoT, CompletedCarT } from '@/core/types/create_order.types'
import { FTLOrderPayloadI, OrderPayloadI } from '@/core/types/orders.types'
import { ReceiverFTLT, ReceiverT } from '@/core/types/receivers.types'
import { FTLBidT } from '@/core/types/ftl_bids.types'
import { AdditionalServicesI } from '@/core/types/additional_services.types'
import { TimeSlotForCreateOrder } from '@/new-services/types/time-slots'

interface ActionsPayloadI {
    id: number | undefined
    //
    title: string | null
    full_name: string | null
    phone: string | null
    additional_phone: string | null
    city_id: number | null
    city: CityT | null
    //
    full_address: string | null
    latitude: number | null
    longitude: number | null
    //
    index: string | null
    office: string | null
    comment: string | null
    street: string | null
    house: string | null
    //
    warehouse_id: number | null
}

interface OrderStateI {
    company_id: number | null
    sender_id: number | null
    sender_city_id: number | null
    sender_title: string | null
    sender_additional_services: AdditionalServicesI
    send_date: string | null
    send_period: number | null
    receiver: ReceiverT | null
    completed_cargos: CompletedCargoT[]
}

interface FTLOrderStateI {
    ftl_bid: FTLBidT | null
    sender_id: number | null
    send_date: string | null
    send_time: string | null
    completed_car: CompletedCarT | null
    receivers: ReceiverFTLT[]
    additional_service: AdditionalServicesI
}

export default function () {
    const { unmaskNumbers } = useMaska()

    function templatePayload(
        actions_payload: ActionsPayloadI,
        company_id: number,
        sender_id?: number,
        distribution_center_id?: number
    ): TemplatePayloadT | void {
        if (
            !actions_payload.title ||
            !actions_payload.city_id ||
            !actions_payload.full_name ||
            !actions_payload.full_address ||
            !actions_payload.phone ||
            !actions_payload.latitude ||
            !actions_payload.longitude
        ) {
            return
        }

        const payload: TemplatePayloadT = {
            title: actions_payload.title,
            entity: actions_payload.title,
            city_id: actions_payload.city_id,
            full_name: actions_payload.full_name,
            full_address: actions_payload.full_address,
            latitude: actions_payload.latitude,
            longitude: actions_payload.longitude,
            phone: '+' + unmaskNumbers(actions_payload.phone),
            additional_phone: actions_payload.additional_phone
                ? '+' + unmaskNumbers(actions_payload.additional_phone)
                : null,
            street: actions_payload.street,
            house: actions_payload.house,
            comment: actions_payload.comment,
            office: actions_payload.office,
            index: actions_payload.index,
            company_id,
        }

        if (actions_payload.id) {
            payload.id = actions_payload.id
        }

        if (sender_id) {
            payload.sender_id = sender_id
        }

        if (distribution_center_id) {
            payload.distribution_center_id = distribution_center_id
        }

        if (actions_payload.warehouse_id) {
            payload.warehouse_id = actions_payload.warehouse_id
        } else {
            payload.warehouse_id = null
        }

        return payload
    }

    function orderPayload(order_state: OrderStateI, timeSlot?: TimeSlotForCreateOrder | null): OrderPayloadI | void {
        if (
            !order_state.company_id ||
            !order_state.sender_title ||
            !order_state.sender_id ||
            !order_state.send_period ||
            !order_state.send_date
        ) {
            return
        }

        const company_id = order_state.company_id
        const title = order_state.sender_title
        const sender_id = order_state.sender_id
        const period_id = order_state.send_period
        const take_date = order_state.send_date

        let takeTimeSlot: OrderPayloadI['takeTimeSlot'] = undefined

        if (timeSlot) {
            takeTimeSlot = {
                templateId: timeSlot.id,
                takeDate: take_date,
            }
        }

        const payload: OrderPayloadI = {
            source: 'BPMS',
            company_id,
            title,
            sender_id,
            additional_service: order_state.sender_additional_services,
            receivers: order_state.completed_cargos.map((completed_cargo) => {
                const cod_payment = completed_cargo.logistics_info.cod_payment
                    ? completed_cargo.logistics_info.cod_payment
                    : '0'
                const declared_price = completed_cargo.logistics_info.declared_price
                    ? completed_cargo.logistics_info.declared_price
                    : '0'

                return {
                    receiver_id: completed_cargo.receiver.id,
                    order_logistics_info: {
                        annotation: completed_cargo.logistics_info.annotation,
                        cod_payment: Number(unmaskNumbers(cod_payment)),
                        declared_price: Number(declared_price),
                        cargo_name: completed_cargo.logistics_info.cargo_name,
                        product_name: completed_cargo.logistics_info.product_name,
                        depth: completed_cargo.logistics_info.depth,
                        dop_invoice_number: completed_cargo.logistics_info.customer_invoice_number,
                        height: completed_cargo.logistics_info.height,
                        payment_method: completed_cargo.logistics_info.payment_method,
                        payment_type: completed_cargo.logistics_info.payment_type,
                        payer_company_id: completed_cargo.logistics_info.payer_company_id || null,
                        places: completed_cargo.logistics_info.places,
                        shipment_type: completed_cargo.logistics_info.shipment_type,
                        should_return_document: completed_cargo.logistics_info.is_return_documents,
                        volume: completed_cargo.logistics_info.volume,
                        weight: completed_cargo.logistics_info.weight,
                        width: completed_cargo.logistics_info.width,
                        take_date,
                        period_id,
                        verify: completed_cargo.logistics_info.verify,
                        //
                        is_dangerous: completed_cargo.logistics_info.is_dangerous,
                        invoice_files: completed_cargo.logistics_info.invoice_files,
                        certificate_of_safety_files: completed_cargo.logistics_info.certificate_of_safety_files,
                        temperature_regime_safety_files: completed_cargo.logistics_info.temperature_regime_safety_files,
                        temperature_regime_type_id: completed_cargo.logistics_info.temperature_regime_type_id,
                        //
                        has_auto_parts: completed_cargo.logistics_info.has_auto_parts,
                        has_battery: completed_cargo.logistics_info.has_battery,
                        msds_file: completed_cargo.logistics_info.msds_file
                    },
                    additional_service: completed_cargo.logistics_info.additional_services,
                    deliveryTimeSlot: completed_cargo.deliveryTimeSlot ? { templateId: completed_cargo.deliveryTimeSlot.templateId, deliveryDate: completed_cargo.deliveryTimeSlot.deliveryDate } : null
                }
            }),
            takeTimeSlot
        }

        return payload
    }

    function FTLOrderPayload(ftl_order_state: FTLOrderStateI): FTLOrderPayloadI | void {
        if (
            !ftl_order_state.ftl_bid ||
            !ftl_order_state.send_date ||
            !ftl_order_state.send_time ||
            !ftl_order_state.sender_id ||
            !ftl_order_state.completed_car
        ) {
            return
        }

        const declared_price = ftl_order_state.completed_car.declared_price
            ? Number(unmaskNumbers(ftl_order_state.completed_car.declared_price.toString()))
            : null

        return {
            ftl_bid_id: ftl_order_state.ftl_bid.id,
            sender_id: ftl_order_state.sender_id,
            take_time: ftl_order_state.send_time,
            take_date: ftl_order_state.send_date,
            annotation: null,
            declared_price,
            payment_method: ftl_order_state.ftl_bid.payment_method,
            car_info: {
                load_capacity: ftl_order_state.completed_car.load_capacity || '',
                cubature: ftl_order_state.completed_car.cubature || '',
                temperature_mode: ftl_order_state.completed_car.temperature_mode || '',
                oversized_cargo: ftl_order_state.completed_car.oversized_cargo || 0,
                loading_type: ftl_order_state.completed_car.loading_type || '',
                payment_method: ftl_order_state.ftl_bid.payment_method,
                transport_type: ftl_order_state.completed_car.transport_type || null,
            },
            is_need_car: ftl_order_state.completed_car.is_need_car || 0,
            receivers: ftl_order_state.receivers,
            additional_service: ftl_order_state.additional_service,
        }
    }

    return { templatePayload, orderPayload, FTLOrderPayload }
}
